import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const baseURL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

  // Check for existing authentication on app load
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Check URL fragment for session_id (from Emergent OAuth)
  useEffect(() => {
    const urlFragment = window.location.hash;
    if (urlFragment.includes('session_id=')) {
      const sessionId = urlFragment.split('session_id=')[1].split('&')[0];
      if (sessionId) {
        processEmergentSession(sessionId);
        // Clean URL fragment
        window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
      }
    }
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await axios.get(`${baseURL}/api/auth/session-data`, {
        withCredentials: true
      });
      
      if (response.data) {
        setUser(response.data);
        setIsAuthenticated(true);
      }
    } catch (error) {
      // No valid session
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const processEmergentSession = async (sessionId) => {
    setLoading(true);
    try {
      const response = await axios.post(`${baseURL}/api/auth/session-data`, {}, {
        headers: { 'X-Session-ID': sessionId },
        withCredentials: true
      });

      if (response.data) {
        setUser(response.data);
        setIsAuthenticated(true);
        
        // Set cookie for session persistence
        document.cookie = `session_token=${response.data.session_token}; path=/; max-age=${7 * 24 * 60 * 60}; secure; samesite=none`;
      }
    } catch (error) {
      console.error('Emergent session processing failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const loginWithPassword = async (email, password) => {
    try {
      const response = await axios.post(`${baseURL}/api/auth/login`, {
        email,
        password
      }, {
        withCredentials: true
      });

      if (response.data.user) {
        setUser(response.data.user);
        setIsAuthenticated(true);
        return { success: true };
      }
      
      return { success: false, error: 'Login failed' };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const loginWithGoogle = () => {
    const redirectUrl = encodeURIComponent(window.location.origin + '/dashboard');
    window.location.href = `https://auth.emergentagent.com/?redirect=${redirectUrl}`;
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${baseURL}/api/auth/register`, userData);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${baseURL}/api/auth/logout`, {}, {
        withCredentials: true
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      // Clear cookie
      document.cookie = 'session_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    }
  };

  const hasPermission = (permission) => {
    return user?.permissions?.includes(permission) || false;
  };

  const isRole = (role) => {
    return user?.role === role;
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    loginWithPassword,
    loginWithGoogle,
    register,
    logout,
    hasPermission,
    isRole,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};